import type { VendorFormResponse, VendorSearchResponse } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { vendorSearchApi } from "../../../../../api/client";
import { MyFormNotListedOption } from "./constants";

/**
 * Debounces type-ahead requests without retaining another query's results.
 * @param value The entered query.
 * @returns {string} The query after a short typing pause.
 */
function useDebouncedSearch(value: string) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value.trim()), 250);
    return () => clearTimeout(timer);
  }, [value]);
  return debounced;
}

/**
 * Shares cached searches between type-ahead and selected-vendor resolution.
 * @param name The vendor search text.
 * @param enabled Whether lookups are allowed.
 * @returns {object} The vendor query configuration.
 */
function vendorQuery(name: string, enabled: boolean) {
  return queryOptions({
    queryKey: ["obp-vendors", name],
    queryFn: async ({ signal }): Promise<VendorSearchResponse[]> => {
      const response = await vendorSearchApi.searchVendors(name, { signal });
      return response.data;
    },
    enabled: enabled && !!name,
    staleTime: 5 * 60_000,
  });
}

/**
 * Looks up vendors by name and their forms by stable vendor code.
 * @param vendorName The current vendor.
 * @param formId The current form identifier.
 * @param formNumber The current display form number.
 * @param readOnly Whether lookups should stay disabled.
 * @returns {object} Search controls, options, loading state and lookup errors.
 */
export function useVendorFormOptions(
  vendorName: string | undefined,
  formId: number | null,
  formNumber: string | undefined,
  readOnly: boolean
) {
  const [vendorSearch, setVendorSearch] = useState("");
  const [formSearch, setFormSearch] = useState("");
  const vendorQueryText = useDebouncedSearch(vendorSearch);
  const formQueryText = useDebouncedSearch(formSearch);
  const vendors = useQuery(vendorQuery(vendorQueryText || vendorName || "", !readOnly));
  const selectedVendor = useQuery(vendorQuery(vendorName || "", !readOnly));
  const vendorCode = selectedVendor.data?.find((vendor) => vendor.vendorName === vendorName)?.vendorCd;
  const forms = useQuery({
    queryKey: ["obp-vendor-forms", vendorCode, formQueryText],
    queryFn: async ({ signal }): Promise<VendorFormResponse[]> => {
      if (!vendorCode) {
        throw new Error("A vendor code is required to load OBP forms.");
      }
      const response = await (formQueryText
        ? vendorSearchApi.searchForms(vendorCode, formQueryText, { signal })
        : vendorSearchApi.getVendorForms(vendorCode, { signal }));
      return response.data;
    },
    enabled: !readOnly && !!vendorCode,
    staleTime: 5 * 60_000,
  });

  const vendorOptions = [
    ...new Set([
      ...(vendorName ? [vendorName] : []),
      ...(vendors.data ?? []).flatMap((vendor) => (vendor.vendorCd && vendor.vendorName ? [vendor.vendorName] : [])),
    ]),
  ].map((name) => ({ label: name, value: name }));
  const formNumberOptions: Array<{ label: string; value: string; formId?: number }> = (forms.data ?? []).flatMap(
    (entry) =>
      typeof entry.formId === "number" && entry.formNumber
        ? [{ label: entry.formNumber, value: String(entry.formId), formId: entry.formId }]
        : []
  );
  // Loading, failed, or partial searches must never erase persisted selections.
  const currentFormValue = formId?.toString() ?? formNumber;
  if (formNumber && currentFormValue && !formNumberOptions.some((option) => option.value === currentFormValue)) {
    formNumberOptions.unshift({ label: formNumber, value: currentFormValue, formId: formId ?? undefined });
  }
  if (!formNumberOptions.some((option) => option.value === MyFormNotListedOption)) {
    formNumberOptions.push({ label: MyFormNotListedOption, value: MyFormNotListedOption });
  }

  return {
    vendorOptions,
    formNumberOptions,
    setVendorSearch,
    setFormSearch,
    vendorLoading: vendors.isFetching,
    formsLoading: selectedVendor.isFetching || forms.isFetching,
    error: vendors.error ?? selectedVendor.error ?? forms.error,
  };
}
